import React, { useLayoutEffect, useRef } from 'react';
import { Button, ImageAtoms } from '@vfit/shared/atoms';
import { purifyTextWithLogo, trackLink } from '@vfit/shared/data-access';
import { purify } from '@vfit/shared/themes';
import {
  MainContainer,
  CardContainer,
  DescriptionBox,
  Title,
  Description,
  CtaContainer,
  BottomDescription,
  ContainerCard,
  ImageContainer,
  EditAddress,
  ContainerAction,
  ImageContainerForCards,
  TitleForCards,
  TopLabelContainer,
} from './splittedProposal.style';
import { IOffersProposalDetail } from '@vfit/consumer/providers';
import { PropositionCard } from '@vfit/consumer/components';
import { updateCardsHeights } from './splittedProposal.utils';

const SplittedProposal: React.FC<IOffersProposalDetail> = ({
  topLogo,
  title,
  description,
  bottomDescription,
  bottomDescriptionAction,
  bottomDescriptionActionLabel,
  firstCard,
  firstCardTopLabel,
  firstCardAction,
  secondCard,
  secondCardAction,
  secondCardTopLabel,
  onCloseModal,
}) => {
  const proposalsElement = useRef<HTMLDivElement>(null);
  const [pTitle, pDescription, pBottomDescription, pFirstCardTopLabel, pSecondCardTopLabel] =
    purify(
      [
        title,
        description || '',
        bottomDescription,
        firstCardTopLabel || '',
        secondCardTopLabel || '',
      ],
      purifyTextWithLogo
    );

  const actionCard = (cardAction) => {
    if (cardAction?.enableCloseOnClickAction) onCloseModal?.();
    cardAction?.action?.();
  };

  const renderCard = (card, cardAction, topLabel?: string) => {
    return (
      <div style={{ width: '100%' }}>
        {topLabel && (
          <TopLabelContainer
            dangerouslySetInnerHTML={{ __html: topLabel }}
            className="splitted-proposal-top-label"
          />
        )}
        <PropositionCard
          price={card?.price}
          cornerColor={card?.cornerColor}
          cornerImage={card?.cornerImage}
          items={card?.items}
          recurrence={card?.recurrence}
          subprice={card?.subprice}
        />
        {cardAction?.label && cardAction?.action && (
          <ContainerAction
            buttonColor={cardAction?.backgroundColor}
            textColor={cardAction?.textColor}
          >
            <Button
              onClick={() => {
                trackLink(cardAction.label);
                actionCard(cardAction);
              }}
            >
              {cardAction.label}
            </Button>
          </ContainerAction>
        )}
      </div>
    );
  };

  const renderTopSection = () => {
    return (
      <>
        {topLogo && (
          <ImageContainerForCards>
            <ImageAtoms nameCard={'top-logo'} image={topLogo} />
          </ImageContainerForCards>
        )}
        {pTitle && <TitleForCards dangerouslySetInnerHTML={{ __html: pTitle }} />}
      </>
    );
  };

  const renderFirstSection = () => {
    if (firstCard && secondCard) {
      return renderCard(firstCard, firstCardAction, pFirstCardTopLabel);
    }
    return (
      <>
        {topLogo && (
          <ImageContainer>
            <ImageAtoms nameCard={'top-logo'} image={topLogo} />
          </ImageContainer>
        )}
        {pTitle && <Title dangerouslySetInnerHTML={{ __html: pTitle }} />}
        {pDescription && <Description dangerouslySetInnerHTML={{ __html: pDescription }} />}
        {pBottomDescription && (
          <CtaContainer>
            <BottomDescription dangerouslySetInnerHTML={{ __html: pBottomDescription }} />
            {bottomDescriptionActionLabel && (
              <EditAddress>
                <span
                  onClick={() => {
                    trackLink(bottomDescriptionActionLabel);
                    bottomDescriptionAction?.();
                    onCloseModal?.();
                  }}
                >
                  {bottomDescriptionActionLabel}
                </span>
              </EditAddress>
            )}
          </CtaContainer>
        )}
      </>
    );
  };

  const renderSecondSection = () => {
    if (firstCard && secondCard) {
      return renderCard(secondCard, secondCardAction, pSecondCardTopLabel);
    }
    return renderCard(firstCard, firstCardAction, pFirstCardTopLabel);
  };

  useLayoutEffect(() => {
    if (proposalsElement?.current) updateCardsHeights(proposalsElement.current);
  }, []);

  return (
    <>
      <MainContainer>
        {firstCard && secondCard && renderTopSection()}
        <CardContainer ref={proposalsElement} haveMargin={!firstCard || !secondCard}>
          <DescriptionBox>{renderFirstSection()}</DescriptionBox>
          <ContainerCard haveMargin={!firstCard || !secondCard}>
            {renderSecondSection()}
          </ContainerCard>
        </CardContainer>
      </MainContainer>
    </>
  );
};

export default SplittedProposal;
